import { Lead, PipelineType, ActivityLogEntry } from '../types';
import { UserRole } from './db';

export function seedActivityLog(lead: Lead): ActivityLogEntry[] {
  const logs: ActivityLogEntry[] = [];
  const baseDate = lead.lastContacted ? new Date(lead.lastContacted) : new Date();

  // Initial creation log
  const creationDate = new Date(baseDate.getTime() - 1000 * 60 * 60 * 24 * 5);
  logs.push({
    id: `log-seed-1-${lead.id}`,
    timestamp: creationDate.toISOString(),
    action: 'Lead Created',
    details: `Imported / initial record created for ${lead.company}`,
    user: 'system@armellini.com',
  });

  // Pipeline Assignment log
  const assignDate = new Date(baseDate.getTime() - 1000 * 60 * 60 * 24 * 3);
  logs.push({
    id: `log-seed-2-${lead.id}`,
    timestamp: assignDate.toISOString(),
    action: 'Stage Configured',
    details: `Assigned stage to "${lead.pipeline}" with status "${lead.status}"`,
    user: lead.owner ? `${lead.owner.toLowerCase().replace(/\s+/g, '.')}@armellini.com` : 'sales-rep',
  });

  // Quote / pricing log if deal value exists
  if (lead.dealValue && lead.dealValue > 0) {
    const quoteDate = new Date(baseDate.getTime() - 1000 * 60 * 60 * 24 * 1);
    logs.push({
      id: `log-seed-3-${lead.id}`,
      timestamp: quoteDate.toISOString(),
      action: 'Quote Configured',
      details: `Configured quote pricing breakdown`,
      user: 'msterling@armellini.com',
    });
  }

  return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

export function getLeadChanges(oldLead: Lead, newLead: Omit<Lead, 'id'> & { id?: string }, userEmail?: string): ActivityLogEntry[] {
  const logs: ActivityLogEntry[] = [];
  const timestamp = new Date().toISOString();
  const user = userEmail || 'sales-rep';

  const compareField = (fieldName: string, label: string) => {
    const oldVal = (oldLead as any)[fieldName] !== undefined ? String((oldLead as any)[fieldName]) : '';
    const newVal = (newLead as any)[fieldName] !== undefined ? String((newLead as any)[fieldName]) : '';
    
    // For quoteDetails, let's compare the nested text part if it's JSON
    if (fieldName === 'quoteDetails') {
      const getQuoteTextOnly = (val: string) => {
        if (!val) return '';
        try {
          if (val.trim().startsWith('{')) {
            const parsed = JSON.parse(val);
            if (typeof parsed === 'object' && parsed !== null) {
              return parsed.text || '';
            }
          }
        } catch(e) {}
        return val;
      };
      const oldQuoteText = getQuoteTextOnly(oldVal);
      const newQuoteText = getQuoteTextOnly(newVal);
      if (oldQuoteText !== newQuoteText) {
        logs.push({
          id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
          timestamp,
          action: 'Quote Pricing Changed',
          details: `Updated from "${oldQuoteText || 'None'}" to "${newQuoteText || 'None'}"`,
          user,
        });
      }
      
      const getQuoteFilesOnly = (val: string): string[] => {
        if (!val) return [];
        try {
          if (val.trim().startsWith('{')) {
            const parsed = JSON.parse(val);
            if (typeof parsed === 'object' && parsed !== null && Array.isArray(parsed.files)) {
              return parsed.files;
            }
          }
        } catch(e) {}
        return [];
      };
      const oldFiles = getQuoteFilesOnly(oldVal);
      const newFiles = getQuoteFilesOnly(newVal);
      if (oldFiles.join(',') !== newFiles.join(',')) {
        logs.push({
          id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
          timestamp,
          action: 'Attachments Modified',
          details: `Files listed: ${newFiles.length > 0 ? newFiles.join(', ') : 'No files attached'}`,
          user,
        });
      }
      return;
    }

    if (oldVal !== newVal) {
      logs.push({
        id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        timestamp,
        action: `${label} Changed`,
        details: `Updated from "${oldVal || 'None'}" to "${newVal || 'None'}"`,
        user,
      });
    }
  };

  compareField('name', 'Name');
  compareField('company', 'Company');
  compareField('email', 'Email');
  compareField('dealValue', 'Deal Value');
  compareField('pipeline', 'Pipeline Category');
  compareField('status', 'Status');
  compareField('owner', 'Owner');
  compareField('contactPerson', 'Contact Person');
  compareField('careOf', 'Care Of');
  compareField('billToAccountNumber', 'Bill-To Account Number');
  compareField('location', 'Location');
  compareField('phone', 'Phone Number');
  compareField('service', 'Service Type');
  compareField('industry', 'Industry');
  compareField('appLink', 'Credit App Link');
  compareField('followUpDate', 'Follow-up Date');
  compareField('quickNotes', 'Quick Notes');

  const oldLeadNotes = (oldLead.leadNotes ?? '').trim();
  const newLeadNotes = (newLead.leadNotes ?? '').trim();
  if (oldLeadNotes !== newLeadNotes) {
    logs.push({
      id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      timestamp,
      action: 'Lead Notes updated',
      details: `Updated from "${oldLeadNotes || 'None'}" to "${newLeadNotes || 'None'}"`,
      user,
    });
  }

  // Compare lanes for additions, approvals, removals, and edits on approved lanes
  const oldLanes = Array.isArray(oldLead.lanes) ? oldLead.lanes : [];
  const newLanes = Array.isArray(newLead.lanes) ? newLead.lanes : [];
  const dateDisplay = new Date(timestamp).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  if (oldLanes.length > 0 || newLanes.length > 0) {
    // Track lane additions
    if (newLanes.length > oldLanes.length) {
      for (let i = oldLanes.length; i < newLanes.length; i++) {
        const added = newLanes[i];
        const laneNum = added.laneNumber || i + 1;
        logs.push({
          id: `log-${Date.now()}-lane-add-${laneNum}-${Math.random().toString(36).substring(2, 6)}`,
          timestamp,
          action: `Lane ${laneNum} Added`,
          details: `Lane ${laneNum}${added.city || added.state ? ` (${[added.city, added.state].filter(Boolean).join(', ')})` : ''} added on ${dateDisplay} by ${user}`,
          user,
        });
      }
    }

    // Track lane removals (match by id when available, or by laneNumber)
    for (const oldLane of oldLanes) {
      const match = newLanes.find((nl) =>
        oldLane.id && nl.id ? String(oldLane.id) === String(nl.id) : nl.laneNumber === oldLane.laneNumber
      );
      if (!match) {
        const laneNum = oldLane.laneNumber;
        const wasApproved = oldLane.approvalStatus === 'approved';
        const locStr = [oldLane.street, oldLane.city, oldLane.state, oldLane.zipCode].filter(Boolean).join(', ') || 'No address';
        logs.push({
          id: `log-${Date.now()}-lane-rm-${laneNum}-${Math.random().toString(36).substring(2, 6)}`,
          timestamp,
          action: wasApproved ? `Approved Lane ${laneNum} Removed` : `Lane ${laneNum} Removed`,
          details: `${wasApproved ? 'Approved ' : ''}Lane ${laneNum} (${locStr} | Quote: ${oldLane.fullQuoteView || 'None'}) removed on ${dateDisplay} by ${user}`,
          user,
        });
      }
    }

    // Track status transitions & edits on approved lanes
    const laneFieldsToCompare: Array<{ key: keyof typeof oldLanes[0]; label: string }> = [
      { key: 'street', label: 'Street Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'zipCode', label: 'Zip Code' },
      { key: 'serviceTypes', label: 'Service Types' },
      { key: 'industries', label: 'Industries' },
      { key: 'outboundStates', label: 'Outbound States' },
      { key: 'outboundWarehouses', label: 'Outbound Warehouses' },
      { key: 'daysOfService', label: 'Days of Service' },
      { key: 'forecastVolume', label: 'Forecast Volume' },
      { key: 'dollarAmount', label: 'Dollar Amount' },
      { key: 'uom', label: 'UOM' },
      { key: 'serviceFrequency', label: 'Service Frequency' },
      { key: 'handlingFee', label: 'Handling Fee' },
      { key: 'noHandlingFee', label: 'No Handling Fee' },
      { key: 'fuelSurchargePercent', label: 'Fuel Surcharge %' },
      { key: 'noFuelSurcharge', label: 'No Fuel Surcharge' },
      { key: 'minimumShipmentAmount', label: 'Minimum Shipment Amount' },
      { key: 'paymentTerms', label: 'Payment Terms' },
      { key: 'depositAmount', label: 'Deposit Amount' },
    ];

    for (const newLane of newLanes) {
      const oldLane = oldLanes.find((ol) =>
        ol.id && newLane.id ? String(ol.id) === String(newLane.id) : ol.laneNumber === newLane.laneNumber
      );
      if (!oldLane) continue;

      const laneNum = newLane.laneNumber;
      if (oldLane.approvalStatus !== 'sent' && newLane.approvalStatus === 'sent') {
        logs.push({
          id: `log-${Date.now()}-lane-sent-${laneNum}-${Math.random().toString(36).substring(2, 6)}`,
          timestamp,
          action: `Lane ${laneNum} Sent to Operations`,
          details: `Lane ${laneNum} sent to Operations on ${dateDisplay} by ${user}`,
          user,
        });
      }
      if (oldLane.approvalStatus !== 'approved' && newLane.approvalStatus === 'approved') {
        logs.push({
          id: `log-${Date.now()}-lane-app-${laneNum}-${Math.random().toString(36).substring(2, 6)}`,
          timestamp,
          action: `Lane ${laneNum} Approved`,
          details: `Lane ${laneNum} Operations / Routing approved on ${dateDisplay} by ${user}`,
          user,
        });
      }

      // If this lane was already approved, log every field change with old & new values, date, and user
      if (oldLane.approvalStatus === 'approved') {
        const formatVal = (v: any) => {
          if (v === undefined || v === null || v === '') return 'None';
          if (Array.isArray(v)) return v.length > 0 ? v.join(', ') : 'None';
          return String(v);
        };
        for (const { key, label } of laneFieldsToCompare) {
          const oldFormatted = formatVal(oldLane[key]);
          const newFormatted = formatVal(newLane[key]);
          if (oldFormatted !== newFormatted) {
            logs.push({
              id: `log-${Date.now()}-lane-edit-${laneNum}-${String(key)}-${Math.random().toString(36).substring(2, 6)}`,
              timestamp,
              action: `Approved Lane ${laneNum} — ${label} Changed`,
              details: `Lane ${laneNum} ${label} updated from "${oldFormatted}" to "${newFormatted}" on ${dateDisplay} by ${user}`,
              user,
            });
          }
        }
      }
    }
  }

  return logs;
}

// Selecting a status moves the lead to its matching board section.
export const STATUS_TO_PIPELINE: Record<string, PipelineType> = {
  'Lead': 'Opportunity',
  'Quoted': 'Opportunity',
  'Approved': 'Won',
  'Finalizing': 'Won',
  'Complete': 'Won',
  'Transferred': 'Unqualified',
  'Declined': 'Unqualified',
  'Not Awarded': 'Lose',
  // Backward compatibility mappings:
  'Awarded': 'Won',
  'Accepted': 'Won',
  'Onboarding': 'Won',
  'Customer Setup': 'Won',
  'New': 'Opportunity',
  'New Lead': 'Opportunity',
  'Website Lead': 'Opportunity',
  'Attempted to Contact': 'Opportunity',
  'Cold Call Lead': 'Opportunity',
  'Sent Quote': 'Opportunity',
  'Sent Credit App': 'Opportunity',
  'In Process': 'Won',
  'Working on it': 'Won',
  'Awaiting First Shipment': 'Won',
  'Won': 'Won',
  'Broker': 'Unqualified',
  'Brokers': 'Unqualified',
  'Shipper': 'Unqualified',
  'Lost': 'Lose',
  'Revisit Later | ADD FU DATE': 'Lose',
};

// Dropping a lead onto a board section gives it that section's default status
export const PIPELINE_TO_STATUS: Record<PipelineType, string> = {
  'Opportunity': 'Lead',
  'Won': 'Approved',
  'Unqualified': 'Declined',
  'Lose': 'Not Awarded',
};

export const BOARDS_CONFIG: { type: PipelineType; color: string }[] = [
  { type: 'Opportunity', color: '#0086f0' },
  { type: 'Won', color: '#00c875' },
  { type: 'Unqualified', color: '#64748b' },
  { type: 'Lose', color: '#e2445c' },
];

export const ROLE_TABS: Record<UserRole, string[]> = {
  admin: ['dashboard', 'contacts', 'cdata-search', 'aelis-logistics', 'analytics', 'google-hub', 'client-forms', 'non-floral', 'help-center', 'profile', 'settings', 'admin-settings'],
  editor: ['dashboard', 'contacts', 'cdata-search', 'aelis-logistics', 'analytics', 'google-hub', 'client-forms', 'non-floral', 'help-center', 'profile', 'settings'],
  sales: ['dashboard', 'contacts', 'cdata-search', 'aelis-logistics', 'analytics', 'google-hub', 'client-forms', 'non-floral', 'help-center', 'profile'],
  warehouse: ['cdata-search', 'aelis-logistics', 'analytics', 'non-floral', 'help-center', 'profile'],
};
